import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../components/layout/MainLayout';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:3001';

function EmployeeCompleteInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { t } = useTranslation();

  const suggestPassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let generated = '';
    for (let i = 0; i < 16; i += 1) {
      generated += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(generated);
    setConfirmPassword(generated);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError(t('employeeInvite.password_mismatch'));
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API_BASE}/auth/employee/complete-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const message = (body as { message?: string }).message
          || t('employeeInvite.error_generic');
        throw new Error(message);
      }
      setSuccess(t('employeeInvite.success_message'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('employeeInvite.error_generic'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <main className="flex flex-1 flex-col items-center justify-center pb-10">
        <section className="glass-panel w-full max-w-md p-6 sm:p-8">
          <h1 className="mb-2 text-xl font-semibold tracking-tight text-slate-900">
            {t('employeeInvite.title')}
          </h1>
          <p className="mb-4 text-sm text-slate-600">
            {t('employeeInvite.subtitle')}
          </p>

          {!token && (
            <p className="text-sm text-red-500">
              {t('employeeInvite.invalid_link')}
            </p>
          )}

          {token && (
            <form className="space-y-4" onSubmit={handleSubmit}>
              {error && (
                <p className="text-xs text-red-500">
                  {error}
                </p>
              )}
              {success && (
                <p className="text-xs text-emerald-600">
                  {success}
                </p>
              )}
              <div className="space-y-1.5 text-sm">
                <label
                  className="block text-xs font-medium text-slate-700"
                  htmlFor="password"
                >
                  {t('employeeInvite.field_password_label')}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="new-password"
                    type={showPassword ? 'text' : 'password'}
                    minLength={8}
                    required
                    autoComplete="new-password"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 pr-9 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-2 flex items-center text-xs text-slate-500 hover:text-slate-700"
                    aria-label={showPassword ? t('employeeInvite.hide_password') : t('employeeInvite.show_password')}
                  >
                    <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                  </button>
                </div>
              </div>
              <div className="space-y-1.5 text-sm">
                <label
                  className="block text-xs font-medium text-slate-700"
                  htmlFor="confirmPassword"
                >
                  {t('employeeInvite.field_password_confirm_label')}
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="new-password-confirm"
                    type={showConfirmPassword ? 'text' : 'password'}
                    minLength={8}
                    required
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 pr-9 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-2 flex items-center text-xs text-slate-500 hover:text-slate-700"
                    aria-label={showConfirmPassword ? t('employeeInvite.hide_password') : t('employeeInvite.show_password')}
                  >
                    <i className={`fa ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={suggestPassword}
                className="text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                {t('employeeInvite.suggest_password', 'Suggest a strong password')}
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center rounded-md bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/80 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? t('employeeInvite.saving_label') : t('employeeInvite.save_button')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="mt-2 w-full text-center text-xs font-medium text-slate-500 hover:text-slate-700"
              >
                {t('employeeInvite.back_to_signin')}
              </button>
            </form>
          )}
        </section>
      </main>
    </MainLayout>
  );
}

export default EmployeeCompleteInvitePage;

